import { useState } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import type { FreeShippingDelivery as FreeShippingAndReturnSchema } from '@amplience/content-types/typings/c-shipping-section';
import type { DeliveryOption } from '../../../codegen/types';
import { useMasterLayoutContext } from '../../../context/masterLayoutContext';
import { useStar2 } from '../../../hooks/useStar2';
import { ArrowDown } from '../../../icons';
import { renderDeliveryOptionsTranslation, transformTextToId } from '../../../utils';
import { capitalizeFirstLetter } from '../../../utils/capitalizeFirstLetter';
import { Loader } from '../../Loader/Loader';
import { RenderPrice } from '../taxAndTotals/CartDelivery';
import {
  AccordionContainer,
  AccordionHeading,
  Container,
  Days,
  DeliveryAccordion,
  DeliveryItem,
  DeliveryLabel,
  DeliveryName,
  DeliveryOptionsWrapper,
  IconWrapper,
  NoDeliveryOptions,
  ShippingSection,
} from './DeliveryOptions.styles';

function DeliveryOptionsList({ deliveryOptions }: { deliveryOptions: DeliveryOption[] | undefined }) {
  const { t } = useTranslation(['lib-global-common']);
  const { isStar2 } = useStar2();
  const noDeliveryOptions = () => <NoDeliveryOptions>{t('cart.delivery.and.returns.error')}</NoDeliveryOptions>;

  if (!deliveryOptions) return <>{noDeliveryOptions()}</>;

  return deliveryOptions?.length > 0 ? (
    <DeliveryOptionsWrapper>
      {deliveryOptions?.map(({ currency, deliveryTime, price, name, key: deliveryKey }) => (
        <DeliveryItem key={deliveryKey}>
          <RenderPrice price={price} currency={currency} />{' '}
          <DeliveryName>{isStar2 ? renderDeliveryOptionsTranslation(deliveryKey, t, name) : name}</DeliveryName>{' '}
          <Days>
            {isStar2
              ? t('cart.delivery.and.returns.deliveryTime', { deliveryTime })
              : capitalizeFirstLetter(deliveryTime)}
          </Days>
        </DeliveryItem>
      ))}
    </DeliveryOptionsWrapper>
  ) : (
    <>{noDeliveryOptions()}</>
  );
}

type DeliveryOptionsProps = {
  deliveryOptions: DeliveryOption[];
  isDeliveryOptionsLoading: boolean;
};

export function DeliveryOptions({ deliveryOptions, isDeliveryOptionsLoading }: DeliveryOptionsProps) {
  const { t } = useTranslation(['lib-global-common']);
  const { masterLayoutData } = useMasterLayoutContext();
  const { freeShippingAndReturn = null } = { ...masterLayoutData };

  const router = useRouter();
  const isPaymentPage = router.asPath.includes('/checkout/payment');
  const isConfirmationPage = router.asPath.includes('/checkout/confirmation');

  const [isAccordionOpen, setIsAccordionOpen] = useState(false);

  const toggleDeliveryAccordion = () => {
    setIsAccordionOpen((prev) => !prev);
  };
  const deliveryLabel = t('cart.delivery.and.returns');

  if (isPaymentPage || isConfirmationPage) return null;

  return (
    <Container>
      <AccordionHeading>
        <DeliveryAccordion
          type="button"
          onClick={toggleDeliveryAccordion}
          aria-expanded={isAccordionOpen}
          aria-controls={`accordion-container-${transformTextToId(deliveryLabel)}`}
          aria-label={deliveryLabel}
        >
          <DeliveryLabel>{deliveryLabel}</DeliveryLabel>
          <IconWrapper $isOpenDelivery={isAccordionOpen}>
            <ArrowDown className="accordion-arrow-down" width={25} aria-hidden alt="" viewBox="0 0 20 20" />
          </IconWrapper>
        </DeliveryAccordion>
      </AccordionHeading>
      <AccordionContainer
        $isOpenDelivery={isAccordionOpen}
        id={`accordion-container-${transformTextToId(deliveryLabel)}`}
        hidden={!isAccordionOpen}
        aria-hidden={!isAccordionOpen}
      >
        <>
          {isDeliveryOptionsLoading ? <Loader isDark /> : <DeliveryOptionsList deliveryOptions={deliveryOptions} />}
          {freeShippingAndReturn && (
            <ShippingSection {...(freeShippingAndReturn as unknown as FreeShippingAndReturnSchema)} />
          )}
        </>
      </AccordionContainer>
    </Container>
  );
}
